import { api } from '@cwd/lib/main';
import type { SmsSent } from '../interfaces';
import type { ContactsResponse } from '@views/Lists/ContactList.access';

export type { ContactsResponse } from '@views/Lists/ContactList.access';

export interface DateRange {
    start: string | null;
    end: string | null;
}

export function buildQueryString(dateRange: DateRange, contactFilter: string | null = null): string {
    const parts: string[] = [];
    if (dateRange.start) {
        parts.push(`start=${encodeURIComponent(new Date(dateRange.start).toISOString())}`);
    }
    if (dateRange.end) {
        const end = new Date(dateRange.end);
        end.setDate(end.getDate() + 1);
        parts.push(`end=${encodeURIComponent(end.toISOString())}`);
    }
    if (contactFilter) {
        // NB: use a non-OData param name ('contactFilter', not '$filter') so the global
        // EnableQueryAttribute does not intercept it on IQueryable-returning aggregate endpoints.
        // The shared filter component pre-encodes for the global OData path (spaces->'+',
        // literal '+'->%2B, '#'->%23). Undo that to get the raw OData expression, then
        // encodeURIComponent the whole value so characters like '&' or '%' in values
        // (e.g. "AT&T", "50%") don't break the query string.
        const rawFilter = contactFilter.replace(/\+/g, ' ').replace(/%2B/g, '+').replace(/%23/g, '#');
        parts.push(`contactFilter=${encodeURIComponent(rawFilter)}`);
    }
    return parts.join('&');
}

export async function getSmsMessage(smsMessageId: string, qs: string, signal?: AbortSignal): Promise<SmsSent> {
    return api.get(`/Textmessages/Sent/${smsMessageId}?${qs}`, { signal });
}

export async function getContacts(smsMessageId: string, contactType: string, qs: string, skip = 0, pageSize = 25, extra = ''): Promise<ContactsResponse> {
    const sep = qs ? '&' : '';
    return api.get(`/Textmessages/${smsMessageId}/Statistics/Contacts/${contactType}?${qs}${sep}$inlinecount=allpages&$skip=${skip}&$top=${pageSize}${extra}`);
}

export function buildContactsUrl(smsMessageId: string, contactType: string, qs: string, extra: string): string {
    const sep = qs ? '&' : '';
    return `/Textmessages/${smsMessageId}/Statistics/Contacts/${contactType}?${qs}${sep}${extra}`;
}

export async function exportContactsExcel(smsMessageId: string, contactType: string, qs: string, extra: string): Promise<{ Url: string }> {
    const url = buildContactsUrl(smsMessageId, contactType, qs, extra);
    return api.get(`${url}&$format=excel`);
}

export async function exportContactsCsv(smsMessageId: string, contactType: string, qs: string, extra: string): Promise<{ Url: string }> {
    const url = buildContactsUrl(smsMessageId, contactType, qs, extra);
    return api.get(`${url}&$format=csv`);
}
